import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * HTTP status code carrying business-exception messages that the backend
 * explicitly intends for the end user. Unlike generic 4xx/5xx errors, the
 * message on a 588 is safe to surface (trimmed and length-capped).
 */
export const BUSINESS_EXCEPTION_STATUS = 588;

/**
 * Maximum length of a surfaced business-exception message. Guards against a
 * stray stack trace or dump being shown to the user if the backend misbehaves.
 */
export const MAX_BUSINESS_MESSAGE_LENGTH = 300;

/**
 * User-friendly, sanitized German messages keyed by HTTP status code (Issue #8).
 * These are the ONLY strings shown to the user for server errors; the raw
 * `error.message` / `error.detail` never reaches the UI.
 */
export const ERROR_USER_MESSAGES: Readonly<Record<number | 'default', string>> = {
    0: 'Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung.',
    400: 'Die Anfrage konnte nicht verarbeitet werden. Bitte prüfen Sie Ihre Eingaben.',
    401: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
    403: 'Sie haben keine Berechtigung für diese Aktion.',
    404: 'Die angeforderten Daten wurden nicht gefunden.',
    408: 'Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.',
    409: 'Die Daten wurden zwischenzeitlich geändert. Bitte laden Sie die Ansicht neu.',
    422: 'Die Eingaben konnten nicht verarbeitet werden. Bitte prüfen Sie Ihre Angaben.',
    500: 'Ein unerwarteter Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
    503: 'Der Dienst ist vorübergehend nicht verfügbar. Bitte versuchen Sie es später erneut.',
    default: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'
};

/**
 * Maps HTTP errors to sanitized, user-facing messages (Issue #8).
 *
 * Security intent: raw server payloads (`error.message`, `error.detail`) can
 * leak backend implementation details — stack traces, SQL fragments, internal
 * IDs, PHI — and must never be shown to the user. This mapper returns a fixed,
 * status-code-based German string instead. The single deliberate exception is
 * status 588 (the business-exception channel), whose message is author-intended
 * for the user; even then it is trimmed and length-capped, with a generic
 * fallback when empty.
 */
@Injectable({ providedIn: 'root' })
export class ErrorMessageMapper {
    toUserMessage(error: HttpErrorResponse): string {
        const status = error?.status;

        if (status === BUSINESS_EXCEPTION_STATUS) {
            return this.businessMessage(error);
        }

        return ERROR_USER_MESSAGES[status] ?? ERROR_USER_MESSAGES.default;
    }

    /**
     * Extracts the business-exception message (status 588), trimmed and capped.
     * Falls back to the generic default when the backend sends nothing usable.
     */
    private businessMessage(error: HttpErrorResponse): string {
        const raw = error?.error?.message;

        if (typeof raw !== 'string') {
            return ERROR_USER_MESSAGES.default;
        }

        const trimmed = raw.trim();
        if (trimmed.length === 0) {
            return ERROR_USER_MESSAGES.default;
        }

        return trimmed.length > MAX_BUSINESS_MESSAGE_LENGTH
            ? trimmed.slice(0, MAX_BUSINESS_MESSAGE_LENGTH)
            : trimmed;
    }
}
